/*
 * Admin helper library: what Vue code in admin uses instead of the global
 * functions of assets/js/support/functions.js.
 *
 *     import { notify, formatLocalTime, api } from '@admin/helpers';
 *
 * Nothing here needs jQuery or any other script on the page.
 */

// The look of notify()'s messages travels with it, in either build.
import { markField, clearField, showSummary, focusField } from './formErrors.js';
import './toast.css';
// So does the look of showLoader()'s overlay.
import './loader.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** True for a string that is a number, as functions.js had it. */
export function isNumeric(str) {
    if (typeof str !== 'string') return false;
    return !isNaN(str) && !isNaN(parseFloat(str));
}

export function isValidDateFormat(str) {
    return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]) (?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(str);
}

/**
 * A date in the viewer's own time zone. `d` is a Date, a Unix timestamp in
 * seconds given as a string, or anything Date() reads; empty means now.
 * Formats: Y-m-d, m/d/Y, H:i, G:i, m/d/Y H:i:s, Y-m-d H:i:s, Y-m-d H:i,
 * "F d, Y", "l, d F Y". No format: "October 11, 2026 at 09:30".
 */
export function formatLocalTime(format, d) {
    if (typeof d === 'string' && isValidDateFormat(d)) {
        d = d.replace(' ', 'T');
    }

    let dateObj;
    if (d instanceof Date) dateObj = d;
    else if (isNumeric(d)) dateObj = new Date(parseInt(d) * 1000);
    else if (d) dateObj = new Date(d);
    else dateObj = new Date();

    const two = (n) => String(n).padStart(2, '0');
    const year = dateObj.getFullYear();
    const month = two(dateObj.getMonth() + 1);
    const date = two(dateObj.getDate());
    const hour = two(dateObj.getHours());
    const minutes = two(dateObj.getMinutes());
    const seconds = two(dateObj.getSeconds());

    switch (format) {
        case 'Y-m-d': return `${year}-${month}-${date}`;
        case 'm/d/Y': return `${month}/${date}/${year}`;
        case 'H:i':
        case 'G:i': return `${hour}:${minutes}`;
        case 'm/d/Y H:i:s': return `${month}/${date}/${year} ${hour}:${minutes}:${seconds}`;
        case 'Y-m-d H:i:s': return `${year}-${month}-${date} ${hour}:${minutes}:${seconds}`;
        case 'Y-m-d H:i': return `${year}-${month}-${date} ${hour}:${minutes}`;
        case 'F d, Y': return `${MONTH_NAMES[dateObj.getMonth()]} ${date}, ${year}`;
        case 'l, d F Y': return `${WEEK_DAYS[dateObj.getDay()]}, ${date} ${MONTH_NAMES[dateObj.getMonth()]} ${year}`;
    }

    if (format) return `${month}/${date}/${year}`;
    return `${MONTH_NAMES[dateObj.getMonth()]} ${date}, ${year} at ${hour}:${minutes}`;
}

/* ---------- Notifications ---------- */

let toastBox = null;

/**
 * A message at the top centre of the screen. `type` is success, error, notice,
 * warning or info. The message may hold simple HTML, as the old
 * append_notification() allowed (validation errors come as <p> lines).
 */
export function notify(type, message, title) {
    if (message === undefined || message === null || message === '') return;
    if (message instanceof Error) message = message.message;

    if (!toastBox || !toastBox.isConnected) {
        toastBox = document.createElement('div');
        toastBox.className = 'ud-toasts';
        toastBox.setAttribute('role', 'status');
        toastBox.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastBox);
    }

    const kind = ['success', 'error', 'warning'].indexOf(type) >= 0 ? type : 'notice';
    const toast = document.createElement('div');
    toast.className = 'ud-toast ud-toast--' + kind;

    // a small mark of the kind: a tick, an exclamation, an "i" (toast.css)
    const icon = document.createElement('span');
    icon.className = 'ud-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<svg viewBox="0 0 24 24">' + (kind === 'success' ? '<path d="M5 12.5l4.5 4.5L19 7.5"/>' : kind === 'notice' ? '<path d="M12 11v6M12 7.2v.1"/>' : '<path d="M12 6.5v7M12 17.3v.1"/>') + '</svg>';

    const text = document.createElement('div');
    text.className = 'ud-toast__text';
    if (title) {
        const strong = document.createElement('strong');
        strong.textContent = title;
        text.appendChild(strong);
    }
    const body = document.createElement('div');
    body.innerHTML = String(message);
    text.appendChild(body);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ud-toast__close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';

    // leaves by flipping up (toast.css, 0.4 s); a second call does nothing
    const remove = () => {
        if (toast.classList.contains('is-leaving')) return;
        toast.classList.add('is-leaving');
        setTimeout(() => toast.remove(), 420);
    };
    close.addEventListener('click', remove);

    toast.appendChild(icon);
    toast.appendChild(text);
    toast.appendChild(close);
    toastBox.appendChild(toast);

    // Errors stay longer: they are the ones people need to read.
    setTimeout(remove, kind === 'error' ? 9000 : 5000);
}

/* ---------- Forms ---------- */

const FORM_RULES = {
    // At least N characters; the "-choose-" of an untouched dropdown is no value.
    hasValue: (value, n) => value !== '-choose-' && value.length >= parseInt(n),
    // vX.Y.Z or vX.Y.Z.A, the name of a repository release.
    validRepoVersion: (value) => /^v+\d+\.\d+\.\d+(\.\d+)?$/.test(value),
};

/**
 * The check behind validateForm(): each field with data-rules is checked; one that fails is marked (formErrors.js:
 * "error" on its .form-line and its message under it), one that passes is cleared. Answers { valid, messages, items }
 * (items: [{ message, field }]).
 */
export function checkForm(root, options = {}) {
    const messages = [];
    const items = [];
    if (!root) return { valid: true, messages, items };

    root.querySelectorAll(options.selector || '[data-rules]').forEach((field) => {
        if (field.disabled) return;

        const rules = String(field.dataset.rules || '').split(';')
            .map((rule) => rule.split(':'))
            .filter((rule) => rule[0] && rule[1] && FORM_RULES[rule[0]]);
        if (!rules.length) return;

        const value = String(field.value || '').trim();
        const failed = rules.findIndex((rule) => !FORM_RULES[rule[0]](value, rule[1]));
        if (failed < 0) {
            clearField(field);
            return;
        }
        // One message per rule, split by ";", when a field has several rules.
        const text = field.dataset.validationMessage || '';
        const message = (rules.length > 1 ? text.split(';')[failed] : text) || text || 'This field is required.';
        markField(field, message);
        const line = field.closest('.form-line');
        if (line) {
            line.classList.add('shake');
            setTimeout(() => line.classList.remove('shake'), 1000);
        }
        messages.push(message);
        items.push({ message, field });
    });

    return { valid: messages.length === 0, messages, items };
}

/**
 * Checks a form's fields that carry data-rules ("hasValue:N", "validRepoVersion:1"). Each wrong field is marked
 * with its own message, a list at the top of the form links to each, and the first one gets the focus.
 *
 *     if (!validateForm(this.$refs.theForm)) event.preventDefault();
 *
 * Options: `selector` narrows the fields checked; `items` are further problems the caller found, as
 * { message, field } (field: an element to mark, or null); `messages` the same as plain texts.
 */
export function validateForm(root, options = {}) {
    if (!root) return true;
    const result = checkForm(root, options);
    const extra = (options.items || []).concat((options.messages || []).map((message) => ({ message, field: null })));
    extra.forEach((item) => { if (item.field) markField(item.field, item.message); });
    const items = result.items.concat(extra);
    // A form in a dialog: the list above the page would be out of sight, so its messages come as a toast
    if (root.closest('dialog,[role="dialog"],[aria-modal="true"],.modal')) {
        if (items.length) notify('error', Array.from(new Set(items.map((item) => item.message))).map((text) => '<p>' + text + '</p>').join(''));
    } else {
        showSummary(root, items);
    }
    if (items.length) {
        const first = items.find((item) => item.field);
        if (first) focusField(first.field);
        else root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return items.length === 0;
}

/* ---------- Page ---------- */

/**
 * Scrolls the page so the element sits `offset` pixels under the top of the
 * window (the fixed top bar takes about 140).
 */
export function scrollToElement(el, offset = 140) {
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    const calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: calm ? 'auto' : 'smooth' });
}

/* ---------- Requests ---------- */

/** The site address with a trailing slash, as the page gives it. */
export function homeUrl() {
    const url = (typeof window !== 'undefined' && window.home_url) || '/';
    return url.endsWith('/') ? url : url + '/';
}

/**
 * Goes to an address by POST instead of a link (15 October 2026): actions that delete or change
 * data refuse a plain GET (refuse_unless_post() on the server). Same page flow as before: the
 * server redirects back with its message. `fields` are sent as hidden inputs.
 *
 *     postTo(homeUrl() + 'spr/delete/' + id);
 */
export function postTo(url, fields = {}) {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = /^https?:\/\//.test(url) ? url : homeUrl() + String(url).replace(/^\//, '');
    form.style.display = 'none';
    Object.keys(fields || {}).forEach((name) => {
        const values = Array.isArray(fields[name]) ? fields[name] : [fields[name]];
        values.forEach((value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = Array.isArray(fields[name]) ? name + '[]' : name;
            input.value = value === null || value === undefined ? '' : String(value);
            form.appendChild(input);
        });
    });
    document.body.appendChild(form);
    form.submit();
}

function toQuery(params, prefix) {
    const parts = [];
    Object.keys(params || {}).forEach((key) => {
        const value = params[key];
        if (value === undefined || value === null) return;
        const name = prefix ? `${prefix}[${Array.isArray(params) ? '' : key}]` : key;
        if (typeof value === 'object') parts.push(toQuery(value, prefix ? `${prefix}[${key}]` : key));
        else parts.push(encodeURIComponent(name) + '=' + encodeURIComponent(value));
    });
    return parts.filter(Boolean).join('&');
}

async function send(method, path, data) {
    let url = /^https?:\/\//.test(path) ? path : homeUrl() + String(path).replace(/^\//, '');
    const options = { method, headers: { 'X-Requested-With': 'XMLHttpRequest' } };

    if (method === 'GET') {
        const query = toQuery(data);
        if (query) url += (url.indexOf('?') >= 0 ? '&' : '?') + query;
    } else if (data instanceof FormData) {
        options.body = data;
    } else {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(data || {});
    }

    const response = await fetch(url, options);
    const text = await response.text();
    let result;
    try {
        result = text ? JSON.parse(text) : null;
    } catch (e) {
        throw new Error('The server answered with something that is not JSON (' + response.status + ')');
    }
    if (!response.ok) {
        throw new Error((result && result.message) || 'Request failed (' + response.status + ')');
    }
    return result;
}

/**
 * Requests to the portal's own AJAX endpoints. Paths are relative to the site
 * address; the answer is the decoded JSON.
 *
 *     const result = await api.get('users/ajax/get_hub_tab', { tab: 'leads' });
 *     await api.post('crm/ajax/change_lead_level', { level: 3, user_ids: [12] });
 *
 * api.postForm() sends the body form-encoded, exactly as $.post did, and gives
 * back the text when the answer is not JSON.
 */
export const api = {
    get: (path, params) => send('GET', path, params),
    post: (path, data) => send('POST', path, data),
    // The same request $.post made: a form-encoded body (see param()).
    postForm: (path, data) => sendForm(path, data),
    query: toQuery,
};

/** $.post as jQuery sent it: a form-encoded body. For endpoints not yet tried with JSON. */
function sendForm(path, data) {
    const url = /^https?:\/\//.test(path) ? path : homeUrl() + String(path).replace(/^\//, '');
    return fetch(url, {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: param(data),
    }).then((response) => response.text().then((text) => {
        let result = text;
        try {
            result = text ? JSON.parse(text) : null;
        } catch (e) {
            // Not JSON: the caller gets the text, as jQuery gave it.
        }
        if (!response.ok) {
            throw new Error((result && result.message) || 'Request failed (' + response.status + ')');
        }
        return result;
    }));
}

/**
 * A query string built the way jQuery.param() built it: `a[]=1` for a list,
 * `a[key]=1` for an object, an empty value for null. What the PHP side has
 * always received from $.post and $.get.
 */
export function param(data) {
    const parts = [];
    const add = (name, value) => {
        if (typeof value === 'function') value = value();
        parts.push(encodeURIComponent(name) + '=' + encodeURIComponent(value === null || value === undefined ? '' : value));
    };
    const build = (prefix, value) => {
        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (/\[\]$/.test(prefix)) add(prefix, item);
                else build(prefix + '[' + (typeof item === 'object' && item !== null ? index : '') + ']', item);
            });
        } else if (value !== null && typeof value === 'object') {
            Object.keys(value).forEach((key) => build(prefix + '[' + key + ']', value[key]));
        } else {
            add(prefix, value);
        }
    };
    Object.keys(data || {}).forEach((key) => build(key, data[key]));
    return parts.join('&');
}

/* ---------- Loading overlay ---------- */

let loaderBox = null;
let loaderTimer = null;

/** Covers the page with a spinner, as $.addLoader() did. */
export function showLoader() {
    clearTimeout(loaderTimer);
    if (!loaderBox || !loaderBox.isConnected) {
        loaderBox = document.createElement('div');
        loaderBox.className = 'ud-loader';
        loaderBox.setAttribute('role', 'status');
        loaderBox.setAttribute('aria-label', 'Loading');
        const spinner = document.createElement('div');
        spinner.className = 'ud-loader__spinner';
        loaderBox.appendChild(spinner);
        document.body.appendChild(loaderBox);
        // Read the layout once so the fade-in runs.
        void loaderBox.offsetWidth;
    }
    loaderBox.classList.add('is-shown');
}

/** Takes the overlay of showLoader() away, as $.removeLoader() did. */
export function hideLoader() {
    if (!loaderBox) return;
    const box = loaderBox;
    box.classList.remove('is-shown');
    clearTimeout(loaderTimer);
    loaderTimer = setTimeout(() => {
        box.remove();
        if (loaderBox === box) loaderBox = null;
    }, 250);
}

/* ---------- Timing ---------- */

/** Runs `fn` once the calls have stopped for `wait` milliseconds. */
export function debounce(wait, fn) {
    let timer = null;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
    };
}

/** Runs `fn` at most once every `wait` milliseconds; the last call is kept. */
export function throttle(wait, fn) {
    let last = 0;
    let timer = null;
    return function (...args) {
        const left = wait - (Date.now() - last);
        clearTimeout(timer);
        if (left <= 0) {
            last = Date.now();
            fn.apply(this, args);
        } else {
            timer = setTimeout(() => {
                last = Date.now();
                fn.apply(this, args);
            }, left);
        }
    };
}

/* ---------- Ported from functions.js, same names and same answers ---------- */

/** $.each: fn(index, value) over a list, fn(key, value) over an object. Returning false stops it. */
export function each(collection, fn) {
    if (!collection) return collection;
    if (Array.isArray(collection) || (typeof collection === 'object' && typeof collection.length === 'number' && !isObject(collection))) {
        for (let i = 0; i < collection.length; i++) {
            if (fn.call(collection[i], i, collection[i]) === false) break;
        }
    } else if (typeof collection === 'object') {
        for (const key of Object.keys(collection)) {
            if (fn.call(collection[key], key, collection[key]) === false) break;
        }
    }
    return collection;
}

/** $.inArray: the position of the value in the list, -1 when it is not there. */
export function inArray(value, list) {
    return list == null ? -1 : Array.prototype.indexOf.call(list, value);
}

/** $.exists: neither null nor empty (loose, as it always was: 0 does not exist). */
export function exists(value) {
    return (value != null && value != '') ? true : false;
}

export function isString(value) {
    return typeof value === 'string' || value instanceof String;
}

export function isArray(value) {
    return value && typeof value === 'object' && value.constructor === Array;
}

export function isObject(value) {
    return value && typeof value === 'object' && value.constructor === Object;
}

export function objectLength(obj) {
    return (isObject(obj)) ? Object.keys(obj).length : 0;
}

export function arrLength(arr) {
    return (typeof arr != 'undefined' && isArray(arr)) ? arr.length : 0;
}

export function IsJsonString(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function random_string(length) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
}

export function ucfirst(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

/** "/" for nothing, the value itself for text, "key: value, …" for a list or an object. */
export function element(string) {
    if (typeof string != 'undefined' && string && typeof string == 'object') {
        const joinArrValues = (arr) => {
            if (typeof arr != 'object') return '';
            let res = '';
            each(arr, (i, v) => {
                if (typeof v == 'object') res += joinArrValues(v);
                else if (typeof i == 'string') res += ((res) ? ', ' : '') + (i + ': ' + v);
                else res += ((res) ? ', ' : '') + v;
            });
            return res;
        };
        return joinArrValues(string);
    }
    return (!string) ? '/' : string;
}

export function currencyLetterSign(currencyCode) {
    if (!currencyCode) return '';
    const code = currencyCode.toUpperCase();
    return code === 'EUR' ? 'EURO' : code;
}

export function priceFromCents(cents) {
    if (cents === undefined || cents === null || isNaN(cents)) return '0.00';
    const value = Number(cents);
    if (isNaN(value)) return '0.00';
    return (value / 100).toFixed(2);
}

export function toMoneyString(number, addSign) {
    if (typeof number == 'string') number = parseFloat(number);

    const sign = number < 0 ? '-' : '';
    const i = String(parseInt(number = Math.abs(Number(number) || 0).toFixed(2)));
    const j = i.length > 3 ? i.length % 3 : 0;

    let res = sign + (j ? i.substr(0, j) + ',' : '') + i.substr(j).replace(/(\.{3})(?=\.)/g, '$1,') + '.' + Math.abs(number - i).toFixed(2).slice(2);
    if (typeof addSign != 'undefined' && addSign) res = '$' + res;
    return res;
}

export function currentTimestamp() {
    return Math.floor(Date.now() / 1000);
}

export function startOfDayTimestamp() {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Math.floor(start.getTime() / 1000);
}

export function differenceInDays(secondDate, firstDate) {
    return Math.round((secondDate - firstDate) / (1000 * 60 * 60 * 24));
}

/** A Unix timestamp in seconds as a local date; the format is formatLocalTime()'s, m/d/Y when left out. */
export function timestampToDate(unix_timestamp, format) {
    if (!unix_timestamp) return false;
    return formatLocalTime(format || 'm/d/Y', new Date(parseInt(unix_timestamp) * 1000));
}

export function isExtensionImageLike(extension) {
    if (!extension || typeof extension !== 'string') return false;
    extension = extension.replace(/^\.+/, '').toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'ico', 'apng', 'avif'].indexOf(extension) > -1;
}

/** $.resourceIconByExtension: the icon markup for a file of that extension. */
export function resourceIconByExtension(extension) {
    const known = ['file', 'xls', 'xlsx', 'docx', 'xml', 'crash', 'eps', 'video', 'mp3', 'mp4', 'mp', 'document', 'txt', 'c', 'cpp', 'cs', 'html', 'html5', 'css3', 'php', 'typescript', 'ts', 'javascript', 'js', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'zip', 'rar', 'pdf', 'wikipedia', 'git', 'library', 'mov', 'MOV'];
    if (extension) {
        extension = extension.replace('.', '');
        if (known.indexOf(extension) > -1) return '<span class="mdi mdi-resources-' + extension + '"></span>';
    }
    return '<span class="mdi mdi-resources-file"></span>';
}

/** The class, icon and text a payment or subscription status is shown with. */
export function buildStatusObj(status) {
    const res = { class: 'pending', icon: 'mdi-radiobox-blank', text: '' };

    if (/active|paid/.test(status)) {
        res.class = 'active';
        res.icon = 'mdi-check-circle';
    } else if (/pending|incomplete|trialing|canceled|unpaid|open|draft/.test(status)) {
        // pending, as set above
    } else if (/past_due|uncollectible|void/.test(status)) {
        res.class = 'past_due';
        res.icon = 'mdi-timelapse';
    }
    res.text = capitalizeFirstLetter(status);
    return res;
}

/** The names in `bitmask_list` whose bit is set in `mask`. */
export function symbology_from_bitwise(mask, bitmask_list) {
    if (!mask || !bitmask_list) return [];
    const bitmask = parseInt(mask);
    const result = [];
    if (bitmask) {
        each(bitmask_list, (k, val) => {
            if ((val & bitmask) > 0) result.push(k);
        });
    }
    return result;
}

/** $.multiArrayReturn: the row of `arr` whose `key` equals `val` (loosely), or its index; false when none. */
export function multiArrayReturn(arr, search_obj_key_name, search_obj_key_val, returnType) {
    if (!returnType) returnType = 'value';

    let result = false;
    let index = -1;
    each(arr, (i, k) => {
        if (typeof k[search_obj_key_name] != 'undefined' && k[search_obj_key_name] == search_obj_key_val) {
            result = arr[i];
            index = i;
        }
    });

    if (returnType == 'value' && result) return result;
    if (returnType == 'index' && index > -1) return index;
    return false;
}

/** $.multiArrayDelete: `arr` without the rows whose `key` equals `val` (loosely). */
export function multiArrayDelete(arr, search_obj_key_name, search_obj_key_val, keep_key_order) {
    const keep = exists(keep_key_order);
    const return_arr = keep ? {} : [];

    each(arr, (i, k) => {
        if (typeof k[search_obj_key_name] != 'undefined' && k[search_obj_key_name] != search_obj_key_val) {
            if (keep) return_arr[i] = k;
            else return_arr.push(k);
        } else if (keep) {
            return_arr[i] = [];
        }
    });
    return return_arr;
}

/**
 * Marks every place `searchedText` appears under the element(s) with
 * <span class="yellow_mark">. Takes an element, a list of elements or a
 * selector (the old one took a jQuery object).
 */
export function highlight_text(target, searchedText) {
    if (!target || !searchedText) return;
    if (typeof target === 'string') target = document.querySelectorAll(target);
    const elements = target instanceof Element ? [target] : Array.from(target);

    elements.forEach((el) => {
        if (el.children.length > 0) {
            Array.from(el.children).forEach((child) => highlight_text(child, searchedText));
        } else {
            let text = searchedText.replace(/(\s+)/, '(<[^>]+>)*$1(<[^>]+>)*');
            let html = el.innerHTML;
            html = html.replace(new RegExp('(' + text + ')', 'gi'), '<span class="yellow_mark">$1</span>');
            html = html.replace(/(<span class="yellow_mark">[^<>]*)((<[^>]+>)+)([^<>]*<\/span>)/, '$1</span>$2<span>$4');
            el.innerHTML = html;
        }
    });

    const words = searchedText.split(' ');
    if (words.length > 1) words.forEach((word) => highlight_text(elements, word));
}

export function http_build_query(formdata, numeric_prefix, arg_separator) {
    
    var value, key, tmp = [];
    
    var _http_build_query_helper = function(key, val, arg_separator) {
        var k, tmp = [];
        if (val === true) {
            val = '1';
        } else if (val === false) {
            val = '0';
        }
        if (val != null) {
            if (typeof val === 'object') {
                for (k in val) {
                    if (val[k] != null) {
                        tmp.push(_http_build_query_helper(key + '[' + k + ']', val[k], arg_separator));
                    }
                }
                return tmp.join(arg_separator);
            } else if (typeof val !== 'function') {
                return encodeURIComponent(key) + '=' + encodeURIComponent(val);
            } else {
                throw new Error('There was an error processing for http_build_query().');
            }
        } else {
            return '';
        }
    };
    
    if (!arg_separator) {
        arg_separator = '&';
    }
    for (key in formdata) {
        value = formdata[key];
        if (numeric_prefix && !isNaN(key)) {
            key = String(numeric_prefix) + key;
        }
        var query = _http_build_query_helper(key, value, arg_separator);
        if (query !== '') {
            tmp.push(query);
        }
    }
    
    var return_string =  tmp.join(arg_separator);
    
    return return_string;
}

export function sprintf () {
    
    var regex = /%%|%(?:(\d+)\$)?((?:[-+#0 ]|'[\s\S])*)(\d+)?(?:\.(\d*))?([\s\S])/g
    var args = arguments
    var i = 0
    var format = args[i++]
    
    var _pad = function (str, len, chr, leftJustify) {
        if (!chr) {
            chr = ' '
        }
        var padding = (str.length >= len) ? '' : new Array(1 + len - str.length >>> 0).join(chr)
        return leftJustify ? str + padding : padding + str
    }
    
    var justify = function (value, prefix, leftJustify, minWidth, padChar) {
        var diff = minWidth - value.length
        if (diff > 0) {
            if (!leftJustify && padChar === '0') {
                value = [
                    value.slice(0, prefix.length),
                    _pad('', diff, '0', true),
                    value.slice(prefix.length)
                ].join('')
            } else {
                value = _pad(value, minWidth, padChar, leftJustify)
            }
        }
        return value
    }
    
    var _formatBaseX = function (value, base, leftJustify, minWidth, precision, padChar) {
        var number = value >>> 0
        value = _pad(number.toString(base), precision || 0, '0', false)
        return justify(value, '', leftJustify, minWidth, padChar)
    }
    
    var _formatString = function (value, leftJustify, minWidth, precision, customPadChar) {
        if (precision !== null && precision !== undefined) {
            value = value.slice(0, precision)
        }
        return justify(value, '', leftJustify, minWidth, customPadChar)
    }
    
    var doFormat = function (substring, argIndex, modifiers, minWidth, precision, specifier) {
        var number, prefix, method, textTransform, value
        
        if (substring === '%%') {
            return '%'
        }
        
        var padChar = ' ' // pad with spaces by default
        var leftJustify = false
        var positiveNumberPrefix = ''
        var j, l
        
        for (j = 0, l = modifiers.length; j < l; j++) {
            switch (modifiers.charAt(j)) {
                case ' ':
                case '0':
                padChar = modifiers.charAt(j)
                break
                case '+':
                positiveNumberPrefix = '+'
                break
                case '-':
                leftJustify = true
                break
                case "'":
                if (j + 1 < l) {
                    padChar = modifiers.charAt(j + 1)
                    j++
                }
                break
            }
        }
        
        if (!minWidth) {
            minWidth = 0
        } else {
            minWidth = +minWidth
        }
        
        if (!isFinite(minWidth)) {
            throw new Error('Width must be finite')
        }
        
        if (!precision) {
            precision = (specifier === 'd') ? 0 : 'fFeE'.indexOf(specifier) > -1 ? 6 : undefined
        } else {
            precision = +precision
        }
        
        if (argIndex && +argIndex === 0) {
            throw new Error('Argument number must be greater than zero')
        }
        
        if (argIndex && +argIndex >= args.length) {
            throw new Error('Too few arguments')
        }
        
        value = argIndex ? args[+argIndex] : args[i++]
        
        switch (specifier) {
            case '%':
            return '%'
            case 's':
            return _formatString(value + '', leftJustify, minWidth, precision, padChar)
            case 'c':
            return _formatString(String.fromCharCode(+value), leftJustify, minWidth, precision, padChar)
            case 'b':
            return _formatBaseX(value, 2, leftJustify, minWidth, precision, padChar)
            case 'o':
            return _formatBaseX(value, 8, leftJustify, minWidth, precision, padChar)
            case 'x':
            return _formatBaseX(value, 16, leftJustify, minWidth, precision, padChar)
            case 'X':
            return _formatBaseX(value, 16, leftJustify, minWidth, precision, padChar)
            .toUpperCase()
            case 'u':
            return _formatBaseX(value, 10, leftJustify, minWidth, precision, padChar)
            case 'i':
            case 'd':
            number = +value || 0
            number = Math.round(number - number % 1)
            prefix = number < 0 ? '-' : positiveNumberPrefix
            value = prefix + _pad(String(Math.abs(number)), precision, '0', false)
            
            if (leftJustify && padChar === '0') {
                padChar = ' '
            }
            return justify(value, prefix, leftJustify, minWidth, padChar)
            case 'e':
            case 'E':
            case 'f': // @todo: Should handle locales (as per setlocale)
            case 'F':
            case 'g':
            case 'G':
            number = +value
            prefix = number < 0 ? '-' : positiveNumberPrefix
            method = ['toExponential', 'toFixed', 'toPrecision']['efg'.indexOf(specifier.toLowerCase())]
            textTransform = ['toString', 'toUpperCase']['eEfFgG'.indexOf(specifier) % 2]
            value = prefix + Math.abs(number)[method](precision)
            return justify(value, prefix, leftJustify, minWidth, padChar)[textTransform]()
            default:
            return ''
        }
    }
    
    try {
        return format.replace(regex, doFormat)
    } catch (err) {
        return false
    }
}
